// Onglet « Traitements ».
//
// Le pendant exact de l'onglet Opérations, et c'est délibéré : deux
// natures, deux catalogues, deux écrans (CLAUDE.md §2bis). Ce qui
// change est ce qui les distingue — un traitement est un ACHAT DE
// SERVICE, donc il porte un mode de facturation et un fournisseur ;
// une opération d'usinage n'a ni l'un ni l'autre, son coût vient du
// temps passé sur une machine.
//
// Ce que cet écran ne porte PAS, volontairement : un prix par défaut.
// Un prix qui apparaît tout seul sur un devis est un prix que personne
// n'a décidé, et « une entrée manquante ne vaut pas zéro ». Le prix se
// saisit sur la ligne de sous-traitance, devis par devis.

import { useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import Modal from '../../ui/Modal.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import SourceDot from '../../ui/SourceDot.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useCrudList } from './useCrudList.js';
import { api } from '../../lib/api.js';

const EMPTY = {
  code: '',
  label: '',
  aliases: [],
  default_mode: 'flat',
  default_supplier_id: null,
  description: '',
  sort_order: 0,
};

const MODES = [
  { value: 'flat', label: 'Forfait pour le lot' },
  { value: 'per_piece', label: 'Au nombre de pièces' },
  { value: 'per_kg', label: 'Au kilo' },
];

const modeLabel = (v) => MODES.find((m) => m.value === v)?.label ?? v;

export function TraitementsPanel({ isAdmin, suppliers = [] }) {
  const list = useCrudList({
    path: '/catalogue/treatments?all=true',
    key: 'treatments',
    label: 'Traitement',
  });
  const [merging, setMerging] = useState(null);
  const pending = list.rows.filter((t) => !t.confirmed);

  const columns = [
    {
      key: 'label',
      label: 'Traitement',
      render: (t) => (
        <span className="row">
          <SourceDot source={t.confirmed ? 'manual' : 'ai'} title="Lu sur un plan, à confirmer" />
          {t.label}
        </span>
      ),
    },
    { key: 'aliases', label: 'Alias', render: (t) => (t.aliases ?? []).join(', ') || '—' },
    { key: 'default_mode', label: 'Facturation', render: (t) => modeLabel(t.default_mode) },
    {
      key: 'default_supplier_id',
      label: 'Fournisseur',
      render: (t) => suppliers.find((s) => s.id === t.default_supplier_id)?.name ?? '—',
    },
    {
      key: 'active',
      label: '',
      render: (t) => (t.active ? null : <Badge tone="neutral">Désactivé</Badge>),
    },
    ...(isAdmin
      ? [
          {
            key: 'actions',
            label: '',
            render: (t) => (
              <span className="row">
                <Button size="sm" variant="ghost" onClick={() => list.setEditing(t)}>
                  Modifier
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMerging(t)}>
                  Fusionner
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Card>
        <CardHead title="Traitements">
          {isAdmin ? (
            <Button size="sm" variant="primary" onClick={() => list.setEditing(EMPTY)}>
              Nouveau traitement
            </Button>
          ) : null}
        </CardHead>
        <CardBody className="stack">
          <p className="field__hint">
            Anodisation, brunissage, microbillage, traitement thermique, découpe laser : ce sont des{' '}
            <strong>achats de service</strong>. Ils entrent sur le devis comme une ligne de coût
            avec marge, pas comme un temps d’usinage. Les variantes d’écriture se fusionnent :
            « ANOD. NAT. », « Anod. natur » et « anodisation naturelle » deviennent une seule ligne
            et deux alias.
          </p>
          <p className="field__hint">
            Comme les opérations, cette liste est un <strong>vocabulaire</strong> — mais la
            frontière n’est pas la même. « Anodisation dure 30 microns » <em>est</em> un type : 30
            microns et 20 microns sont deux bains, deux prix, parfois deux fournisseurs. Le chiffre
            appartient au traitement. « Anodisation dure sur la face avant » n’en est pas un : la
            face avant appartient à <em>cette</em> pièce.
          </p>
          <p className="field__hint">
            Un libellé inconnu lu sur un plan vous est <strong>proposé</strong> sur l’écran de
            chiffrage, il n’entre ici qu’après votre validation.
          </p>
          <p className="field__hint">
            Aucun prix ici, volontairement : il se saisit sur le devis. Tant qu’il manque, la pièce
            reste incomplète — un traitement non chiffré ne doit jamais partir à 0 €.
          </p>
          {pending.length > 0 ? (
            <p className="field__hint">
              <strong>{pending.length}</strong> traitement(s) attendent une relecture.
            </p>
          ) : null}

          {list.loading ? (
            <SkeletonRows rows={4} />
          ) : list.error ? (
            <ErrorState error={list.error} onRetry={list.reload} />
          ) : (
            <Table
              columns={columns}
              rows={list.rows}
              caption="Catalogue des traitements"
              empty={
                <EmptyState title="Aucun traitement">
                  Rien à saisir. Le catalogue se construira à partir des premiers plans lus.
                </EmptyState>
              }
            />
          )}
        </CardBody>
      </Card>

      {list.editing ? (
        <TraitementForm
          treatment={list.editing}
          suppliers={suppliers}
          errors={list.errors}
          pending={list.pending}
          onClose={() => list.setEditing(null)}
          onSave={list.save}
        />
      ) : null}

      {merging ? (
        <MergeForm
          treatment={merging}
          candidates={list.rows.filter((t) => t.id !== merging.id)}
          onClose={() => setMerging(null)}
          onDone={() => {
            setMerging(null);
            list.reload();
          }}
        />
      ) : null}
    </>
  );
}

function TraitementForm({ treatment, suppliers, errors, pending, onClose, onSave }) {
  const [form, setForm] = useState({ ...treatment, aliases: treatment.aliases ?? [] });
  const [aliasText, setAliasText] = useState((treatment.aliases ?? []).join(', '));
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <Modal
      open
      onClose={onClose}
      title={treatment.id ? form.label : 'Nouveau traitement'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button variant="primary" type="submit" form="form-traitement" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <form
        id="form-traitement"
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(
            { ...form, aliases: aliasText.split(',').map((a) => a.trim()).filter(Boolean) },
            treatment.id,
          );
        }}
      >
        {treatment.id && !treatment.confirmed ? (
          <p className="field__hint">Lu sur un plan. L’enregistrer le confirme.</p>
        ) : null}
        <TextField label="Libellé" required value={form.label ?? ''} error={errors.label}
          onChange={(e) => set({ label: e.target.value })} />
        <div className="grid-2">
          <Select label="Facturation habituelle" value={form.default_mode ?? 'flat'}
            options={MODES} error={errors.default_mode}
            onChange={(e) => set({ default_mode: e.target.value })} />
          <Select label="Fournisseur habituel"
            value={form.default_supplier_id ? String(form.default_supplier_id) : ''}
            error={errors.default_supplier_id}
            options={[{ value: '', label: '—' },
              ...suppliers.map((s) => ({ value: String(s.id), label: s.name }))]}
            onChange={(e) =>
              set({ default_supplier_id: e.target.value ? Number(e.target.value) : null })} />
        </div>
        <TextField label="Alias" value={aliasText}
          hint="Séparés par des virgules. Servent à reconnaître le traitement sur un plan. Une fusion y ajoute le libellé absorbé."
          onChange={(e) => setAliasText(e.target.value)} />
        <TextField label="Description" value={form.description ?? ''} error={errors.description}
          onChange={(e) => set({ description: e.target.value })} />
      </form>
    </Modal>
  );
}

function MergeForm({ treatment, candidates, onClose, onDone }) {
  const toast = useToast();
  const [targetId, setTargetId] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const res = await api.post(`/catalogue/treatments/${treatment.id}/fusion`, {
        target_id: Number(targetId),
      });
      toast.success(
        `« ${treatment.label} » fusionné. ${res.movedLines} ligne(s) basculée(s) sur « ${res.treatment.label} ».`,
      );
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Fusionner « ${treatment.label} »`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button variant="primary" type="submit" form="form-fusion-traitement" disabled={busy || !targetId}>
            {busy ? 'Fusion…' : 'Fusionner'}
          </Button>
        </>
      }
    >
      <form id="form-fusion-traitement" className="stack" onSubmit={submit}>
        <p className="field__hint">
          « {treatment.label} » disparaîtra et deviendra un <strong>alias</strong> du traitement
          choisi — c’est ce qui évite que l’agent le recrée au plan suivant. Les lignes de devis qui
          l’utilisent basculent : aucun devis passé ne perd son traitement.
        </p>
        <Select label="Conserver à la place" required value={targetId}
          options={[{ value: '', label: '— Choisir —' },
            ...candidates.map((t) => ({ value: String(t.id), label: t.label }))]}
          onChange={(e) => setTargetId(e.target.value)} />
      </form>
    </Modal>
  );
}

export default TraitementsPanel;
